import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Upload, CircleAlert } from "lucide-react";
import { StoreShell } from "@/components/store/StoreShell";
import { BankDetails, UpiQr } from "@/components/store/BankDetails";
import { OtpLogin } from "@/components/store/OtpLogin";
import { ShareOrderWhatsApp } from "@/components/store/WhatsApp";
import { Input } from "@/components/ui/input";
import { useStore } from "@/context/StoreContext";
import { api, errMsg, inr, imgUrl, STATUS_LABEL, STATUS_FLOW, PAY_METHOD, PAY_STATUS, REFUND_STATUS } from "@/lib/api";

const Timeline = ({ status }) => {
  if (status === "cancelled") return <p className="text-accent font-bold" data-testid="order-cancelled">This order was cancelled.</p>;
  const idx = STATUS_FLOW.indexOf(status);
  return (
    <ol className="grid grid-cols-5 gap-1" data-testid="order-timeline">
      {STATUS_FLOW.map((s, i) => (
        <li key={s} className="flex flex-col items-center text-center gap-2">
          <span className={`w-8 h-8 rounded-full flex items-center justify-center ${i <= idx ? "bg-secondary text-white" : "bg-stone-200 text-stone-400"}`}>
            {i <= idx ? <Check className="w-4 h-4" /> : i + 1}
          </span>
          <span className={`text-[11px] sm:text-xs font-semibold ${i <= idx ? "text-primary" : "text-stone-400"}`}>{STATUS_LABEL[s]}</span>
        </li>
      ))}
    </ol>
  );
};

const ProofForm = ({ order, onDone }) => {
  const [utr, setUtr] = useState(order.payment_ref || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("utr", utr);
    if (file) fd.append("file", file);
    try { onDone((await api.post(`/orders/${order.id}/payment-proof`, fd)).data); toast.success("Payment details submitted"); setFile(null); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="payment-proof-form">
      <BankDetails amount={order.amount_due} />
      <Input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="UTR / reference number" className="h-12 rounded-xl" data-testid="proof-utr-input" />
      <label className="flex items-center gap-3 rounded-xl border-2 border-dashed border-stone-300 p-4 cursor-pointer hover:border-primary/50 transition-colors" data-testid="proof-file-dropzone">
        {file ? <img src={URL.createObjectURL(file)} alt="preview" className="w-16 h-16 rounded-lg object-cover" /> : <Upload className="w-6 h-6 text-stone-400" strokeWidth={1.5} />}
        <span className="text-sm text-stone-600">{file ? file.name : "Upload payment screenshot (JPG/PNG, max 5 MB)"}</span>
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="proof-file-input" />
      </label>
      <button onClick={submit} disabled={busy || (!utr.trim() && !file)} className="h-12 px-8 rounded-full bg-primary text-white font-bold disabled:opacity-40 active:scale-95 transition-transform" data-testid="proof-submit-btn">
        {busy ? "Submitting…" : "Submit payment details"}
      </button>
    </div>
  );
};

export default function OrderDetail() {
  const { id } = useParams();
  const { customer, config } = useStore();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!customer) return;
    api.get(`/orders/${id}`).then((r) => setO(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id, customer]);

  const cancel = async () => {
    if (!window.confirm("Cancel this order?")) return;
    try { setO((await api.post(`/orders/${id}/cancel`)).data); toast.success("Order cancelled"); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!customer) return <StoreShell><div className="max-w-md mx-auto px-4 py-16"><h1 className="font-display text-4xl text-primary mb-6">Verify to view your order</h1><OtpLogin /></div></StoreShell>;
  if (err) return <StoreShell><p className="max-w-md mx-auto px-4 py-16 text-accent" data-testid="order-error">{err}</p></StoreShell>;
  if (!o) return <StoreShell><p className="max-w-md mx-auto px-4 py-16 text-stone-500">Loading…</p></StoreShell>;

  const needsProof = o.payment_method === "bank_transfer" && ["awaiting_proof", "rejected", "proof_submitted"].includes(o.payment_status) && o.status !== "cancelled";

  return (
    <StoreShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12 space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs tracking-[0.1em] uppercase text-stone-500 font-bold">Order</p>
            <h1 className="font-display text-4xl sm:text-5xl text-primary font-semibold" data-testid="order-number">{o.order_number}</h1>
            <p className="text-sm text-stone-600 mt-1" data-testid="order-slot">{o.delivery_date === config?.today ? "Delivery today" : `Delivery on ${o.delivery_date}`} · {o.slot_label}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ShareOrderWhatsApp order={o} />
            <Link to="/orders" className="text-sm font-semibold text-primary hover:underline" data-testid="back-to-orders">All orders</Link>
          </div>
        </div>

        <section className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 sm:p-7"><Timeline status={o.status} /></section>

        {o.cod_approval === "pending" && o.status !== "cancelled" && (
          <div className="flex gap-2 rounded-2xl bg-accent/10 text-accent p-4 text-sm font-semibold" data-testid="cod-approval-pending-msg">
            <CircleAlert className="w-4 h-4 mt-0.5" /> Waiting for store approval: this is your first cash-on-delivery order, so we'll call you to confirm before packing.
          </div>
        )}
        {o.payment_status === "rejected" && (
          <div className="flex gap-2 rounded-2xl bg-accent/10 text-accent p-4 text-sm font-semibold" data-testid="payment-rejected-msg">
            <CircleAlert className="w-4 h-4 mt-0.5" /> We couldn't verify your payment. Please re-check the UTR or upload a clear screenshot.
          </div>
        )}

        <section className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 sm:p-7">
          <h2 className="font-display text-2xl text-primary font-semibold mb-4">Items</h2>
          <div className="divide-y divide-stone-100">
            {o.items.map((it, i) => (
              <div key={i} className="flex items-center gap-3 py-3" data-testid={`order-item-${i}`}>
                <img src={imgUrl(it.image)} alt={it.name} className={`w-12 h-12 rounded-lg object-cover ${it.status === "unavailable" ? "grayscale opacity-60" : ""}`} />
                <div className="flex-1">
                  <p className={`font-semibold ${it.status === "unavailable" ? "line-through text-stone-400" : "text-primary"}`}>{it.name}{it.cut && <span className="ml-1.5 text-xs font-bold text-accent" data-testid={`order-item-cut-${i}`}>{it.cut}{it.cut_charge ? ` (+${inr(it.cut_charge)})` : ""}</span>}</p>
                  <p className="text-xs text-stone-500">{it.unit} · {inr(it.price)} × {it.qty}</p>
                  {it.status === "unavailable" && <p className="text-xs text-accent font-bold" data-testid={`order-item-unavailable-${i}`}>Unavailable · {inr(it.refund_amount)} refunded</p>}
                </div>
                <span className="font-bold">{inr(it.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-100 pt-4 mt-2 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{inr(o.subtotal)}</span></div>
            <div className="flex justify-between"><span>Delivery fee</span><span data-testid="order-delivery-fee">{o.delivery_fee ? inr(o.delivery_fee) : "Free"}</span></div>
            {o.refund_total > 0 && (
              <div className="flex justify-between text-accent font-semibold" data-testid="order-refund-line"><span>Refund for unavailable items ({REFUND_STATUS[o.refund_status]})</span><span>− {inr(o.refund_total)}</span></div>
            )}
            <div className="flex justify-between text-lg font-extrabold text-primary"><span>{o.payment_method === "bank_transfer" ? "Final amount" : "Amount to pay"}</span><span data-testid="order-amount-due">{inr(o.amount_due)}</span></div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 sm:p-7 space-y-3">
          <h2 className="font-display text-2xl text-primary font-semibold">Payment</h2>
          <p className="text-sm"><b>{PAY_METHOD[o.payment_method]}</b> · <span data-testid="order-payment-status">{PAY_STATUS[o.payment_status]}</span></p>
          {o.payment_ref && <p className="text-sm text-stone-600">UTR: <b data-testid="order-utr">{o.payment_ref}</b></p>}
          {o.payment_method === "upi_delivery" && o.status !== "cancelled" && o.payment_status !== "paid" && (
            <div className="space-y-2"><p className="text-sm text-stone-600">Pay via UPI on delivery.</p><UpiQr amount={o.amount_due} /></div>
          )}
          {needsProof && <ProofForm order={o} onDone={setO} />}
        </section>

        <section className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 sm:p-7 text-sm text-stone-600">
          <p><b className="text-primary">{o.name}</b> · +91 {o.phone}</p>
          <p>{o.address.line1}{o.address.landmark ? `, ${o.address.landmark}` : ""}, {o.area_name} {o.pincode}</p>
        </section>

        {o.status === "placed" && (
          <button onClick={cancel} className="text-sm font-semibold text-accent hover:underline" data-testid="cancel-order-btn">Cancel order</button>
        )}
      </div>
    </StoreShell>
  );
}
